import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Users } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  UNIFORM_FUNCTION_VALUES,
  type UniformMemberSummaryType,
} from '@aeris/contracts'
import {
  Checkbox,
  EmptyState,
  InlineAlert,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { ROLE_LABELS } from '../components/role-labels.ts'
import {
  fetchUniformMembers,
  replaceUniformMemberFunctions,
} from '../lib/api/uniforms.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  FUNCTION_LABELS,
  type UniformFunction,
} from '../lib/production-stage-meta.tsx'

/**
 * A equipe da produção — quem atende, quem desenha, quem imprime (F171).
 *
 * A função não é o papel: a pessoa continua Vendedor (ou Gerente) para o resto
 * do sistema, e aqui ganha o que faz na 4Play. É isto que decide em que parte
 * do pedido ela mexe e o que aparece em "Minha fila". Quem gerencia a produção
 * mexe em tudo sem precisar de função.
 *
 * Cada caixinha salva sozinha: é uma lista curta, e um botão "Salvar" no fim
 * seria mais um jeito de a mudança ficar pela metade.
 */

const MembersPanel = (): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canManage = allows('uniforms.manage')
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['uniform-members'],
    queryFn: async ({ signal }) => await fetchUniformMembers(signal),
  })

  const save = useMutation({
    mutationFn: async (input: {
      member: UniformMemberSummaryType
      functions: UniformFunction[]
    }) => {
      await replaceUniformMemberFunctions(input.member.userId, {
        functions: input.functions,
      })
    },
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['uniform-members'] })
      await queryClient.invalidateQueries({ queryKey: ['uniform-viewer'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar as funções.'
      )
    },
  })

  if (query.isPending) return <Spinner label="Carregando a equipe" />
  if (query.isError) {
    return (
      <InlineAlert tone="danger">
        Não foi possível carregar a equipe.
      </InlineAlert>
    )
  }

  const members = query.data
  if (members.length === 0) {
    return (
      <EmptyState
        icon={<Users aria-hidden="true" className="h-8 w-8" />}
        title="Ninguém na equipe"
        description="Convide as pessoas em Configurações → Equipe; elas aparecem aqui para receber a função."
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Pessoa</TableHeaderCell>
              <TableHeaderCell>Papel</TableHeaderCell>
              {UNIFORM_FUNCTION_VALUES.map((fn) => (
                <TableHeaderCell key={fn}>
                  {FUNCTION_LABELS[fn].person}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.userId}>
                <TableCell className="font-(--weight-medium)">
                  {member.displayName}
                </TableCell>
                <TableCell className="text-(--text-secondary)">
                  {ROLE_LABELS[member.role]}
                </TableCell>
                {UNIFORM_FUNCTION_VALUES.map((fn) => (
                  <TableCell key={fn}>
                    <Checkbox
                      aria-label={`${FUNCTION_LABELS[fn].person}: ${member.displayName}`}
                      disabled={!canManage || save.isPending}
                      checked={member.functions.includes(fn)}
                      onCheckedChange={(checked) => {
                        save.mutate({
                          member,
                          functions: UNIFORM_FUNCTION_VALUES.filter((option) =>
                            option === fn
                              ? checked === true
                              : member.functions.includes(option)
                          ),
                        })
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export const EquipeProducaoPage = (): ReactNode => (
  <>
    <TabHeader
      title="Equipe da produção"
      description={
        <>
          O que cada pessoa faz no pedido: <strong>Atendente</strong> fecha a
          ficha e a grade com o cliente, <strong>Designer</strong> faz a arte e
          diz quando foi aprovada, <strong>Operador</strong> leva o pedido pela
          fábrica. Todos veem o pedido inteiro; cada um mexe na sua parte. Quem
          gerencia mexe em tudo.
        </>
      }
    />

    <MembersPanel />
  </>
)
